import React from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Separator,
  Button,
} from '@lb-classroom/ui';
import { X } from 'lucide-react';
import classnames from 'classnames';
import TextInput from '@components/UI/atoms/form/TextInput';
import Select from '@components/UI/atoms/form/Select';
import RadioInputs from '@components/UI/atoms/form/RadioInputs';
import ModalMessage from '@components/UI/organisms/ModalMessage';
import type { EditCertificationItemModalProps } from './edit-certification-item-modal.types';

export const EditCertificationItemModal = ({
  isNew,
  isSubmitting,
  hasChanges,
  type,
  customTitle,
  institution,
  yearFrom,
  yearTo,
  typeOptions,
  yearOptions,
  typeValidation,
  customTitleValidation,
  yearRangeValidation,
  message,
  messageType,
  onTypeChange,
  onCustomTitleChange,
  onInstitutionChange,
  onYearFromChange,
  onYearToChange,
  onSubmit,
  onClose,
}: EditCertificationItemModalProps) => {
  const title = isNew ? 'Add Certification' : 'Edit Certification';
  const wrapperClass = classnames('max-w-lg mx-auto', {
    loading: isSubmitting,
  });

  const typeSelectProps = {
    options: typeOptions,
    className: '',
    id: 'certification__type',
    value: type,
    stateType: 'type',
    name: 'type',
    updateHandler: (value: string) => onTypeChange(value),
    invalidLabel: true,
    onKeyDown: onSubmit,
  };

  const customTitleProps = {
    placeholder: 'Certification Title',
    className: 'certification__custom_title',
    value: customTitle,
    stateType: 'customTitle',
    name: 'customTitle',
    type: 'text',
    updateHandler: (value: string) => onCustomTitleChange(value),
  };

  const institutionProps = {
    placeholder: '',
    className: 'certification__institution',
    value: institution,
    stateType: 'institution',
    name: 'institution',
    type: 'text',
    updateHandler: (value: string) => onInstitutionChange(value),
  };

  const yearFromProps = {
    options: yearOptions,
    name: 'certification__year_from',
    className: 'certification__year_from',
    id: 'certification__year_from_select',
    value: yearFrom || '',
    updateHandler: (value: string | number) => {
      onYearFromChange(value === '' ? null : Number(value));
    },
    blockClass: true,
    validationResult: null,
    invalidLabel: false,
  };

  const yearToProps = {
    options: yearOptions,
    name: 'certification__year_to',
    className: 'certification__year_to',
    id: 'certification__year_to_select',
    value: yearTo || '',
    updateHandler: (value: string | number) => {
      onYearToChange(value === '' ? null : Number(value));
    },
    blockClass: true,
    validationResult: null,
    invalidLabel: false,
  };

  const renderTypePart = () => {
    if (!isNew) {
      return null;
    }

    return (
      <div className="actions-wrapper mb-8">
        <label className="h5">Certification Title</label>
        <p>
          You can add other certification, but it should be related to education
          or one which can draw attention of some students. For example, you can
          add a certification related to programming since there are many
          students working as computer engineer.
        </p>
        <RadioInputs {...typeSelectProps} />
        <label className="invalid">{typeValidation.message}</label>
      </div>
    );
  };

  return (
    <div className={wrapperClass}>
      <Card className="z-50">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{title}</CardTitle>
          <button type="button" className="modal-close" onClick={onClose}>
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </CardHeader>
        <CardContent>
          <ModalMessage message={message} messageType={messageType} />
          <form
            method="put"
            className="report-issue-form"
            onSubmit={onSubmit}
            noValidate
          >
            <div>
              {renderTypePart()}
              <div className="grid grid-cols-12 gap-4 mb-8">
                <div className="col-span-8">
                  <label className="h5">Custom Title(Optional)</label>
                  <p>
                    This title will be shown to the students instead of the
                    {'"Certification Title"'}.
                  </p>
                  <TextInput {...customTitleProps} />
                  <label className="invalid">
                    {customTitleValidation.message}
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4 mb-8">
                <div className="col-span-8">
                  <label className="h5">Institution Name (Optional)</label>
                  <TextInput {...institutionProps} />
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4 mb-8">
                <div className="col-span-6">
                  <label className="h5">Duration</label>
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-6">
                      <label>From</label>
                      <Select {...yearFromProps} />
                    </div>
                    <div className="col-span-6">
                      <label>To</label>
                      <Select {...yearToProps} />
                    </div>
                  </div>
                  <label className="invalid">
                    {yearRangeValidation.message}
                  </label>
                </div>
              </div>
            </div>
            <Separator className="my-4" />
            <div className="text-right">
              <Button
                size="lg"
                disabled={isSubmitting || !hasChanges}
                onClick={onSubmit}
              >
                Save
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
